import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx, tokens } from '../../theme/tokens';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';

type Rgb = { r: number; g: number; b: number };

type RalEntry = {
  code: string;
  name: string;
  hex: string;
};

type MixEntry = {
  id: string;
  hex: string;
  weight: string;
};

const DEFAULT_RAL: RalEntry[] = [
  { code: 'RAL 1003', name: 'Signalgelb', hex: '#F7BA0B' },
  { code: 'RAL 2004', name: 'Reinorange', hex: '#E25303' },
  { code: 'RAL 3000', name: 'Feuerrot', hex: '#A72920' },
  { code: 'RAL 3003', name: 'Rubinrot', hex: '#7E1D25' },
  { code: 'RAL 4005', name: 'Blaulila', hex: '#76689A' },
  { code: 'RAL 5002', name: 'Ultramarinblau', hex: '#20214F' },
  { code: 'RAL 5015', name: 'Himmelblau', hex: '#007CB0' },
  { code: 'RAL 6005', name: 'Moosgruen', hex: '#2C5545' },
  { code: 'RAL 6018', name: 'Gelbgruen', hex: '#57A639' },
  { code: 'RAL 7001', name: 'Silbergrau', hex: '#8A9597' },
  { code: 'RAL 7016', name: 'Anthrazitgrau', hex: '#293133' },
  { code: 'RAL 7035', name: 'Lichtgrau', hex: '#CBD0CC' },
  { code: 'RAL 9001', name: 'Cremeweiss', hex: '#E9E0D2' },
  { code: 'RAL 9005', name: 'Tiefschwarz', hex: '#0A0A0A' },
  { code: 'RAL 9016', name: 'Verkehrsweiss', hex: '#F1F0EA' }
];

const clampChannel = (value: number, min = 0, max = 255) => Math.min(max, Math.max(min, Math.round(value)));

const parseHex = (value: string): Rgb | null => {
  const trimmed = value.trim().replace('#', '');
  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(trimmed)) return null;
  const full = trimmed.length === 3
    ? trimmed.split('').map((c) => c + c).join('')
    : trimmed;
  const num = Number.parseInt(full, 16);
  return {
    r: (num >> 16) & 0xff,
    g: (num >> 8) & 0xff,
    b: num & 0xff
  };
};

const rgbToHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b].map((v) => clampChannel(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

const shiftRgb = (rgb: Rgb, delta: number): Rgb => ({
  r: clampChannel(rgb.r + delta),
  g: clampChannel(rgb.g + delta),
  b: clampChannel(rgb.b + delta)
});

const rgbToHsl = ({ r, g, b }: Rgb) => {
  const nr = r / 255;
  const ng = g / 255;
  const nb = b / 255;
  const max = Math.max(nr, ng, nb);
  const min = Math.min(nr, ng, nb);
  const delta = max - min;
  let h = 0;
  if (delta !== 0) {
    if (max === nr) h = ((ng - nb) / delta) % 6;
    if (max === ng) h = (nb - nr) / delta + 2;
    if (max === nb) h = (nr - ng) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s: s * 100, l: l * 100 };
};

const hslToRgb = (h: number, s: number, l: number): Rgb => {
  const sat = s / 100;
  const light = l / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = light - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h >= 0 && h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return {
    r: clampChannel((rp + m) * 255),
    g: clampChannel((gp + m) * 255),
    b: clampChannel((bp + m) * 255)
  };
};

const formatNumber = (value: number, digits = 1): string =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(value);

const nearestRal = (rgb: Rgb, list: RalEntry[]): { entry: RalEntry | null; dist: number } => {
  let best: RalEntry | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  list.forEach((entry) => {
    const match = parseHex(entry.hex);
    if (!match) return;
    const dist = Math.sqrt(
      Math.pow(rgb.r - match.r, 2) +
      Math.pow(rgb.g - match.g, 2) +
      Math.pow(rgb.b - match.b, 2)
    );
    if (dist < bestDist) {
      bestDist = dist;
      best = entry;
    }
  });
  return { entry: best, dist: bestDist };
};

const parseCustomRal = (raw: string): RalEntry[] => {
  const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const entries: RalEntry[] = [];
  lines.forEach((line) => {
    const parts = line.split(/[;,\t]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return;
    const codeMatch = parts[0].match(/RAL\s*\d{4}/i);
    const hexMatch = parts.find((p) => /^#?[0-9a-fA-F]{6}$/.test(p));
    if (!codeMatch || !hexMatch) return;
    const code = codeMatch[0].toUpperCase().replace(/\s+/, ' ');
    const name = parts.find((p) => p !== codeMatch[0] && p !== hexMatch) || 'Custom';
    const hex = hexMatch.startsWith('#') ? hexMatch.toUpperCase() : `#${hexMatch.toUpperCase()}`;
    entries.push({ code, name, hex });
  });
  return entries;
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewFarbCodex } = useAuth();
  const [rgb, setRgb] = useState<Rgb>({ r: 178, g: 20, b: 15 });
  const [hexInput, setHexInput] = useState('#B2140F');
  const [ralCode, setRalCode] = useState('');
  const [customRalRaw, setCustomRalRaw] = useState('');
  const [customRal, setCustomRal] = useState<RalEntry[]>([]);
  const [mixEntries, setMixEntries] = useState<MixEntry[]>([
    { id: 'a', hex: '#B2140F', weight: '1' },
    { id: 'b', hex: '#0D3559', weight: '1' }
  ]);

  const ralList = useMemo(() => {
    const merged = [...DEFAULT_RAL, ...customRal];
    const map = new Map<string, RalEntry>();
    merged.forEach((entry) => map.set(entry.code, entry));
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [customRal]);

  const hsl = useMemo(() => rgbToHsl(rgb), [rgb]);
  const hex = useMemo(() => rgbToHex(rgb), [rgb]);
  const nearest = useMemo<{ entry: RalEntry | null; dist: number }>(() => nearestRal(rgb, ralList), [rgb, ralList]);

  const handleHexChange = (value: string) => {
    setHexInput(value);
    const parsed = parseHex(value);
    if (parsed) setRgb(parsed);
  };

  const handleRgbChange = (key: keyof Rgb, value: string) => {
    const num = Number.parseFloat(value.replace(',', '.'));
    if (!Number.isFinite(num)) return;
    const next = { ...rgb, [key]: clampChannel(num) } as Rgb;
    setRgb(next);
    setHexInput(rgbToHex(next));
  };

  const handleHslChange = (key: 'h' | 's' | 'l', value: string) => {
    const num = Number.parseFloat(value.replace(',', '.'));
    if (!Number.isFinite(num)) return;
    const next = { h: hsl.h, s: hsl.s, l: hsl.l, [key]: num } as { h: number; s: number; l: number };
    const nextRgb = hslToRgb(Math.max(0, Math.min(360, next.h)), Math.max(0, Math.min(100, next.s)), Math.max(0, Math.min(100, next.l)));
    setRgb(nextRgb);
    setHexInput(rgbToHex(nextRgb));
  };

  const handleRalSelect = (code: string) => {
    setRalCode(code);
    const entry = ralList.find((item) => item.code === code);
    if (entry) {
      const parsed = parseHex(entry.hex);
      if (parsed) {
        setRgb(parsed);
        setHexInput(rgbToHex(parsed));
      }
    }
  };

  const applyCustomRal = () => {
    setCustomRal(parseCustomRal(customRalRaw));
  };

  const updateMixEntry = (id: string, patch: Partial<MixEntry>) => {
    setMixEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  };

  const addMixEntry = () => {
    const id = `mix_${Date.now()}`;
    setMixEntries((prev) => [...prev, { id, hex: '#FFFFFF', weight: '1' }]);
  };

  const removeMixEntry = (id: string) => {
    setMixEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const mixedColor = useMemo(() => {
    let totalWeight = 0;
    let acc = { r: 0, g: 0, b: 0 };
    mixEntries.forEach((entry) => {
      const weight = Number.parseFloat(entry.weight.replace(',', '.'));
      const parsed = parseHex(entry.hex);
      if (!parsed || !Number.isFinite(weight) || weight <= 0) return;
      totalWeight += weight;
      acc = {
        r: acc.r + parsed.r * weight,
        g: acc.g + parsed.g * weight,
        b: acc.b + parsed.b * weight
      };
    });
    if (totalWeight === 0) return null;
    return {
      r: clampChannel(acc.r / totalWeight),
      g: clampChannel(acc.g / totalWeight),
      b: clampChannel(acc.b / totalWeight)
    };
  }, [mixEntries]);

  const applyMix = () => {
    if (!mixedColor) return;
    setRgb(mixedColor);
    setHexInput(rgbToHex(mixedColor));
  };

  const conversionSteps = useMemo(() => {
    const fromHex = parseHex(hexInput);
    if (!fromHex) return [] as string[];
    return [
      `1) HEX aufteilen: ${hexInput.toUpperCase()} -> R=${fromHex.r}, G=${fromHex.g}, B=${fromHex.b}`,
      `2) Normieren: r=${formatNumber(fromHex.r / 255, 3)}, g=${formatNumber(fromHex.g / 255, 3)}, b=${formatNumber(fromHex.b / 255, 3)}`,
      `3) HSL aus RGB: H=${formatNumber(hsl.h, 1)}°, S=${formatNumber(hsl.s, 1)}%, L=${formatNumber(hsl.l, 1)}%`,
      nearest.entry
        ? `4) Naechster RAL ueber euklidische Distanz: ${nearest.entry.code} (${nearest.entry.name}), Distanz=${formatNumber(nearest.dist, 2)}`
        : '4) Kein passender RAL-Eintrag gefunden.'
    ];
  }, [hexInput, hsl.h, hsl.l, hsl.s, nearest.dist, nearest.entry]);

  const tonalPalette = useMemo(() => {
    const shifts = [-100, -60, -30, 0, 30, 60, 100];
    return shifts.map((delta) => rgbToHex(shiftRgb(rgb, delta)));
  }, [rgb]);

  if (!canViewFarbCodex) {
    return (
      <UnitPageShell title="Farb-Codex">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="M99 U9 - Farb-Codex">
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' } }}>
        <Stack spacing={3}>
          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="h5" fontWeight={700}>Farb Mixer</Typography>
                    <Tooltip title="Alle Farbräume sind bidirektional gekoppelt: HEX, RGB, HSL und RAL.">
                      <IconButton size="small" aria-label="Info Farbmixer"><InfoOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Farbe frei definieren wie in Paint (Color-Picker), numerisch feinjustieren und in andere Formate umrechnen.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                    border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <Tooltip title="Freie Farbwahl wie in klassischen Grafiktools.">
                      <Box
                        component="input"
                        type="color"
                        value={hex}
                        onChange={(e) => handleHexChange(e.target.value)}
                        sx={{
                          width: 72,
                          height: 48,
                          border: `1px solid ${darkMode ? tokens.colors.dark.borderStrong : tokens.colors.light.borderStrong}`,
                          borderRadius: 1,
                          background: 'transparent',
                          p: 0.25
                        }}
                      />
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                      Freie Definition: Waehlen Sie eine Farbe per Picker und verfeinern Sie danach RGB/HSL oder RAL.
                    </Typography>
                  </Stack>
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Hex"
                    value={hexInput}
                    onChange={(e) => handleHexChange(e.target.value)}
                    helperText="3- oder 6-stellige HEX-Notation"
                    fullWidth
                  />
                  <TextField
                    label="R"
                    value={rgb.r}
                    onChange={(e) => handleRgbChange('r', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="G"
                    value={rgb.g}
                    onChange={(e) => handleRgbChange('g', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="B"
                    value={rgb.b}
                    onChange={(e) => handleRgbChange('b', e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="H"
                    value={formatNumber(hsl.h, 1)}
                    onChange={(e) => handleHslChange('h', e.target.value)}
                    helperText="0..360"
                    fullWidth
                  />
                  <TextField
                    label="S (%)"
                    value={formatNumber(hsl.s, 1)}
                    onChange={(e) => handleHslChange('s', e.target.value)}
                    helperText="0..100"
                    fullWidth
                  />
                  <TextField
                    label="L (%)"
                    value={formatNumber(hsl.l, 1)}
                    onChange={(e) => handleHslChange('l', e.target.value)}
                    helperText="0..100"
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel id="ral-select">RAL</InputLabel>
                    <Select
                      labelId="ral-select"
                      label="RAL"
                      value={ralCode}
                      onChange={(e) => handleRalSelect(e.target.value)}
                    >
                      <MenuItem value="">-</MenuItem>
                      {ralList.map((entry) => (
                        <MenuItem key={entry.code} value={entry.code}>
                          {entry.code} - {entry.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                    border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 90,
                        height: 90,
                        borderRadius: 2,
                        border: `1px solid ${darkMode ? tokens.colors.dark.borderStrong : tokens.colors.light.borderStrong}`,
                        background: hex
                      }}
                    />
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle1" fontWeight={700}>Aktuelle Farbe</Typography>
                      <Typography variant="body2" color="text.secondary">Hex: {hex}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        RGB: {rgb.r}, {rgb.g}, {rgb.b}
                      </Typography>
                      {nearest.entry !== null && (
                        <Typography variant="body2" color="text.secondary">
                          Nächste RAL: {nearest.entry.code} ({nearest.entry.name}), Distanz {formatNumber(nearest.dist, 1)}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>

                {conversionSteps.length > 0 && (
                  <Box
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                      border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="subtitle2" color="text.secondary">Rechenweg / Umrechnung</Typography>
                        <Tooltip title="Zeigt den aktuellen Umrechnungsweg von HEX über RGB/HSL bis zur naechsten RAL-Farbe.">
                          <IconButton size="small" aria-label="Info Rechenweg"><InfoOutlinedIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                      {conversionSteps.map((line) => (
                        <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>Misch-Tabelle</Typography>
                {mixEntries.map((entry) => (
                  <Stack key={entry.id} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <TextField
                      label="Hex"
                      value={entry.hex}
                      onChange={(e) => updateMixEntry(entry.id, { hex: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="Gewicht"
                      value={entry.weight}
                      onChange={(e) => updateMixEntry(entry.id, { weight: e.target.value })}
                      fullWidth
                    />
                    <Button variant="outlined" onClick={() => removeMixEntry(entry.id)}>
                      Entfernen
                    </Button>
                  </Stack>
                ))}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <Button variant="outlined" onClick={addMixEntry}>Farbe hinzufuegen</Button>
                  <Button variant="contained" onClick={applyMix} disabled={!mixedColor}>Mix anwenden</Button>
                </Stack>
                {mixedColor && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 2,
                        border: `1px solid ${darkMode ? tokens.colors.dark.borderStrong : tokens.colors.light.borderStrong}`,
                        background: rgbToHex(mixedColor)
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Mix: {rgbToHex(mixedColor)} / RGB({mixedColor.r}, {mixedColor.g}, {mixedColor.b})
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>Farbpalette</Typography>
                  <Tooltip title="Klickbare Swatches aus der RAL-Liste. Wählt sofort eine Farbe aus.">
                    <IconButton size="small" aria-label="Info Palette"><InfoOutlinedIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(82px, 1fr))' }}>
                  {ralList.slice(0, 30).map((entry) => (
                    <Button
                      key={`${entry.code}_${entry.hex}`}
                      variant="outlined"
                      onClick={() => handleHexChange(entry.hex)}
                      sx={{ p: 0.6, minHeight: 58, justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: 0.8, border: '1px solid rgba(0,0,0,0.2)', background: entry.hex }} />
                        <Typography variant="caption" sx={{ lineHeight: 1.2, textAlign: 'left' }}>{entry.code.replace('RAL ', '')}</Typography>
                      </Stack>
                    </Button>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">Erste 30 RAL-Farben als Schnellpalette.</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Stack spacing={3}>
          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>RAL Tabellen</Typography>
                <Typography variant="body2" color="text.secondary">
                  Standardliste ist enthalten. Optional koennen eigene Tabellen als CSV/TSV hinzugefuegt werden:
                  "RAL 9010;Reinweiss;#F6F6F6".
                </Typography>
                <TextField
                  label="Custom RAL Liste"
                  value={customRalRaw}
                  onChange={(e) => setCustomRalRaw(e.target.value)}
                  multiline
                  minRows={6}
                />
                <Button variant="outlined" onClick={applyCustomRal}>Custom Tabelle laden</Button>
                <Divider />
                <Typography variant="body2" color="text.secondary">Geladene Farben: {ralList.length}</Typography>
                <Divider />
                <Typography variant="h6" fontWeight={700}>Tonal-Palette (aktuelle Farbe)</Typography>
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(7, minmax(40px, 1fr))' }}>
                  {tonalPalette.map((hexTone) => (
                    <Tooltip key={hexTone} title={hexTone}>
                      <Button
                        variant="outlined"
                        onClick={() => handleHexChange(hexTone)}
                        sx={{ minWidth: 0, p: 0.5, height: 40, borderColor: 'transparent' }}
                      >
                        <Box sx={{ width: '100%', height: 24, borderRadius: 1, border: '1px solid rgba(0,0,0,0.2)', background: hexTone }} />
                      </Button>
                    </Tooltip>
                  ))}
                </Box>
                <Divider />
                <Typography variant="h6" fontWeight={700}>So verwenden</Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">1) Farbe frei per Picker setzen (wie in Paint).</Typography>
                  <Typography variant="body2" color="text.secondary">2) Feinjustierung ueber HEX, RGB oder HSL.</Typography>
                  <Typography variant="body2" color="text.secondary">3) Optional RAL direkt waehlen oder naechste RAL pruefen.</Typography>
                  <Typography variant="body2" color="text.secondary">4) Mehrere Farben in der Misch-Tabelle gewichten und Mix anwenden.</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </UnitPageShell>
  );
};

export default Page;
